import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { useMediaitemUpload } from '@hooks/useMediaitemUpload';
import { UploadIcon, XIcon } from '@icons';
import { MediaitemUsageType } from '@models';
import { BackgroundJobActivityTarget } from '@models/backgroundActivities';
import { setupForDark } from '@theme';
import InputErrorMessage from './InputErrorMessage';
import MediaitemInlineStatus from './MediaitemInlineStatus';

const MediaItemInlineInput = ({
  target,
  placeholder,
  className,
  labelClassName,
  allowedFileTypes,
  maxFileSize,
  mediaitemUsageType,
  onUploaded,
  disabled = false
}: {
  target: BackgroundJobActivityTarget | null;
  placeholder?: string;
  className?: string;
  labelClassName?: string;
  allowedFileTypes?: string[];
  maxFileSize?: number;
  mediaitemUsageType: MediaitemUsageType;
  onUploaded: (mediaitemId: number, originalFilename?: string) => void;
  disabled?: boolean;
}) => {
  const { t } = useTranslation();
  const {
    inputId,
    fileInputRef,
    onChange,
    uploadProgress,
    error,
    acceptString,
    cancel,
    isUploading
  } = useMediaitemUpload({
    target,
    allowedFileTypes,
    maxFileSize,
    mediaitemUsageType,
    onUploaded
  });

  return (
    <div className={className}>
      <input
        className='hidden'
        type='file'
        ref={fileInputRef}
        multiple={false}
        onChange={onChange}
        accept={acceptString}
        id={inputId}
        disabled={isUploading || disabled}
      />
      {!isUploading && (
        <label
          htmlFor={disabled ? undefined : inputId}
          aria-disabled={disabled || undefined}
          className={classNames(
            'flex items-center justify-center gap-xs p-sm self-stretch',
            'rounded-xs border border-dashed transition-colors',
            disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-secondary',
            setupForDark('border-border-dark'),
            labelClassName
          )}
        >
          <UploadIcon size={24} className={classNames('shrink-0', setupForDark('text-primary'))} />
          <span className={classNames('P2R truncate', setupForDark('text-primary'))}>
            {placeholder || t('domains.tasks.blocks.contentItem.uploadButtonText')}
          </span>
        </label>
      )}
      {isUploading && (
        <MediaitemInlineStatus
          action={
            <button
              type='button'
              className={classNames(
                'p-xxs rounded-full',
                setupForDark('text-icon-primary hover:bg-secondary')
              )}
              onClick={cancel}
              aria-label={t('components.fileInput.cancel')}
            >
              <XIcon size={16} />
            </button>
          }
        >
          <span className='P2R truncate'>
            {t('components.fileInput.progress', { progress: uploadProgress ?? 0 })}
          </span>
        </MediaitemInlineStatus>
      )}
      {error && <InputErrorMessage error={error} />}
    </div>
  );
};

export default MediaItemInlineInput;
